import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Network, ArrowRight, Loader2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";

/**
 * FederationStatusPulse
 * ─────────────────────
 * One-line nudge on the BCM Mission Control screen showing how many
 * Identity Federation sources are configured. Auto-hides the moment
 * every source is configured (no dead-pixel banner once onboarding is
 * complete). Read-only consumer of the existing `/api/federation/status`
 * endpoint — no new backend, no new data path, no RBAC change.
 *
 * Visible to anyone who can already reach the Federation page
 * (admin / bcm_manager / department_head). All other roles see nothing.
 */
const FEDERATION_ROLES = new Set(["admin", "bcm_manager", "department_head"]);

export default function FederationStatusPulse() {
  const { api, user } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!FEDERATION_ROLES.has(user?.role)) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/federation/status");
        if (!cancelled) setStatus(r.data);
      } catch {
        // Stay silent on the home screen if the call fails — never block
        // Mission Control on a non-critical pulse.
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [api, user?.role]);

  if (!FEDERATION_ROLES.has(user?.role)) return null;
  if (loading) return null;
  if (!status) return null;

  const sources = status.sources || [];
  const total = sources.length;
  const configured = sources.filter((s) => s.configured).length;

  // Auto-hide when fully configured — the whole point is to disappear
  // once onboarding is complete.
  if (total === 0 || configured >= total) return null;

  const pct = total > 0 ? Math.round((configured / total) * 100) : 0;
  const tone = configured === 0 ? "amber" : "sky";
  const palette =
    tone === "amber"
      ? {
          border: "border-amber-300",
          bg: "bg-gradient-to-r from-amber-50 to-white",
          icon: "text-amber-700 bg-amber-100",
          dot: "bg-amber-500",
          text: "text-amber-900",
          bar: "bg-amber-500",
          link: "text-amber-800 hover:text-amber-900",
        }
      : {
          border: "border-sky-300",
          bg: "bg-gradient-to-r from-sky-50 to-white",
          icon: "text-sky-700 bg-sky-100",
          dot: "bg-sky-500",
          text: "text-sky-900",
          bar: "bg-sky-500",
          link: "text-sky-800 hover:text-sky-900",
        };

  const unconfiguredNames = sources
    .filter((s) => !s.configured)
    .map((s) => s.name.replace(/\s*\/.*$/, "").trim())
    .join(" · ");

  return (
    <div
      className={`flex items-center gap-3 border ${palette.border} ${palette.bg} rounded-xl px-4 py-2.5 shadow-sm`}
      data-testid="federation-status-pulse"
    >
      <span className={`p-1.5 rounded-lg ${palette.icon} flex-shrink-0`}>
        <Network className="w-4 h-4" />
      </span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="relative flex h-2 w-2 flex-shrink-0">
            <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${palette.dot} opacity-60`} />
            <span className={`relative inline-flex rounded-full h-2 w-2 ${palette.dot}`} />
          </span>
          <p className={`text-sm font-semibold ${palette.text}`} data-testid="federation-pulse-headline">
            <span className="font-extrabold">{configured} of {total}</span> system{total === 1 ? "" : "s"} connected (optional)
          </p>
          <span className="hidden md:inline text-xs text-slate-500 truncate">
            · still pending: {unconfiguredNames}
          </span>
        </div>
        <div className="mt-1.5 h-1 w-full max-w-md rounded-full bg-slate-200 overflow-hidden">
          <div className={`h-full ${palette.bar} transition-all`} style={{ width: `${pct}%` }} />
        </div>
      </div>
      <Link
        to="/federation"
        className={`text-xs font-semibold ${palette.link} inline-flex items-center gap-1 flex-shrink-0`}
        data-testid="federation-pulse-cta"
      >
        Connect Your Systems <ArrowRight className="w-3 h-3" />
      </Link>
    </div>
  );
}
